import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Plus, Trash2, Shield, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

const SCOPE_TYPES = [
  { value: "class", label: "By Class" },
  { value: "semester", label: "By Semester" },
  { value: "student", label: "By Student" },
  { value: "exam_type", label: "By Exam Type" },
];

const VISIBILITY_STATUSES = [
  { value: "published", label: "Published", color: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  { value: "visible", label: "Visible", color: "bg-primary/10 text-primary border-primary/20" },
  { value: "hidden", label: "Hidden", color: "bg-destructive/10 text-destructive border-destructive/20" },
  { value: "restricted", label: "Restricted", color: "bg-amber-500/10 text-amber-700 border-amber-200" },
  { value: "unpublished", label: "Unpublished", color: "bg-muted text-muted-foreground border-border" },
];

function getStatusBadge(status: string) {
  const config = VISIBILITY_STATUSES.find((s) => s.value === status) ?? VISIBILITY_STATUSES[4];
  return <Badge variant="outline" className={cn("text-[10px]", config.color)}>{config.label}</Badge>;
}

export function ResultVisibilityManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    scope_type: "class",
    scope_value: "",
    visibility_status: "published",
    exam_type: "",
    reason: "",
    starts_at: "",
    ends_at: "",
  });

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["result-visibility-rules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("result_visibility_rules")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch classes from courses table (academic module source)
  const { data: classes = [] } = useQuery({
    queryKey: ["academic-classes-for-visibility"],
    queryFn: async () => {
      const { data } = await supabase.from("courses").select("class").not("class", "is", null);
      const unique = [...new Set((data ?? []).map((c) => c.class).filter(Boolean))].sort();
      return unique as string[];
    },
  });

  // Fetch students for dropdown
  const { data: students = [] } = useQuery({
    queryKey: ["students-for-visibility"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("id, first_name, last_name, student_number, class").order("first_name");
      return data ?? [];
    },
  });

  const createRule = useMutation({
    mutationFn: async () => {
      if (!form.scope_value || !user) throw new Error("Please fill required fields");
      const { error } = await supabase.from("result_visibility_rules").insert({
        scope_type: form.scope_type,
        scope_value: form.scope_value,
        visibility_status: form.visibility_status,
        exam_type: form.exam_type || null,
        reason: form.reason || null,
        set_by: user.id,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["result-visibility-rules"] });
      toast({ title: "Visibility rule created" });
      setDialogOpen(false);
      setForm({ scope_type: "class", scope_value: "", visibility_status: "published", exam_type: "", reason: "", starts_at: "", ends_at: "" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteRule = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("result_visibility_rules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["result-visibility-rules"] });
      toast({ title: "Rule deleted" });
    },
  });

  const getScopeLabel = (type: string, value: string) => {
    if (type === "student") {
      const s = students.find((st) => st.id === value);
      return s ? `${s.first_name} ${s.last_name} (${s.student_number})` : value;
    }
    return value;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Result Visibility Control
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1 text-xs">
                <Plus className="h-3 w-3" /> Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-sm">Create Visibility Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Scope</Label>
                    <Select value={form.scope_type} onValueChange={(v) => setForm({ ...form, scope_type: v, scope_value: "" })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SCOPE_TYPES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={form.visibility_status} onValueChange={(v) => setForm({ ...form, visibility_status: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VISIBILITY_STATUSES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">
                    {form.scope_type === "class" ? "Class" : form.scope_type === "semester" ? "Semester" : form.scope_type === "student" ? "Student" : "Exam Type"}
                  </Label>
                  {form.scope_type === "class" ? (
                    <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select class" /></SelectTrigger>
                      <SelectContent>
                        {classes.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : form.scope_type === "semester" ? (
                    <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select semester" /></SelectTrigger>
                      <SelectContent>
                        {["I", "II"].map((s) => <SelectItem key={s} value={s} className="text-xs">Semester {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : form.scope_type === "student" ? (
                    <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select student" /></SelectTrigger>
                      <SelectContent>
                        {students.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="text-xs">
                            {s.first_name} {s.last_name} — {s.student_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select exam type" /></SelectTrigger>
                      <SelectContent>
                        {["CAT I", "CAT II", "ES I", "ES II"].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {form.scope_type !== "exam_type" && (
                  <div>
                    <Label className="text-xs">Exam Type (optional filter)</Label>
                    <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All exam types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">All</SelectItem>
                        {["CAT I", "CAT II", "ES I", "ES II"].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Reason (optional)</Label>
                  <Textarea
                    className="text-xs min-h-[60px]"
                    placeholder="Why is this rule being set?"
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Starts at (optional)</Label>
                    <Input type="datetime-local" className="h-8 text-xs" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Ends at (optional)</Label>
                    <Input type="datetime-local" className="h-8 text-xs" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
                  </div>
                </div>

                <Button size="sm" className="w-full" onClick={() => createRule.mutate()} disabled={createRule.isPending || !form.scope_value}>
                  {createRule.isPending ? "Creating…" : "Create Rule"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Control which results students can see. Rules are evaluated in priority order: Student → Class → Semester → Default.
          Financial clearance is checked automatically after manual rules.
        </p>
      </CardHeader>
      <CardContent>
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mb-3">
          {VISIBILITY_STATUSES.map((s) => {
            const count = rules.filter((r: any) => r.visibility_status === s.value).length;
            return count > 0 ? (
              <Badge key={s.value} variant="outline" className={cn("text-[10px] gap-1", s.color)}>
                {s.label}: {count}
              </Badge>
            ) : null;
          })}
        </div>

        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-muted rounded" />)}
          </div>
        ) : rules.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-sm">
            <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
            No visibility rules configured. By default, all results are hidden from students.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Scope</TableHead>
                  <TableHead className="text-xs">Value</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Exam Type</TableHead>
                  <TableHead className="text-xs">Reason</TableHead>
                  <TableHead className="text-xs">Period</TableHead>
                  <TableHead className="text-xs w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule: any) => (
                  <TableRow key={rule.id}>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">{rule.scope_type}</Badge>
                    </TableCell>
                    <TableCell className="text-xs font-medium">{getScopeLabel(rule.scope_type, rule.scope_value)}</TableCell>
                    <TableCell>{getStatusBadge(rule.visibility_status)}</TableCell>
                    <TableCell className="text-xs">{rule.exam_type || "All"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{rule.reason || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {rule.starts_at || rule.ends_at
                        ? `${rule.starts_at ? format(new Date(rule.starts_at), "dd MMM") : "∞"} → ${rule.ends_at ? format(new Date(rule.ends_at), "dd MMM") : "∞"}`
                        : "Always"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => deleteRule.mutate(rule.id)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
